import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { AlertTriangle } from "lucide-react";

export const StartFreshCard = () => {
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const erase = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/settings/reset-data", { confirm });
      toast.success(`All data erased — ${data.removed.customers} customers and ${data.removed.orders} orders removed. You're starting fresh.`);
      navigate("/");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
      setConfirm("");
    }
  };

  return (
    <Card className="shadow-sm border-destructive/30" data-testid="start-fresh-card">
      <CardHeader>
        <CardTitle className="font-serif text-2xl flex items-center gap-2 text-destructive"><AlertTriangle className="h-5 w-5" /> Start fresh</CardTitle>
        <p className="text-sm text-muted-foreground">
          Permanently erase every customer, order, import, duplicate review, group, email campaign, statement job and audit entry,
          and remove every team account except yours. Your settings (categories, statement details, Shopify and email connections) are kept.
          Use this once to clear sample data before real use.
        </p>
      </CardHeader>
      <CardContent>
        <AlertDialog onOpenChange={(open) => !open && setConfirm("")}>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" data-testid="start-fresh-btn">Erase all data…</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif">Erase everything and start fresh?</AlertDialogTitle>
              <AlertDialogDescription>
                This cannot be undone. Type <strong>ERASE</strong> below to confirm.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-1.5">
              <Label htmlFor="erase-confirm">Confirmation</Label>
              <Input id="erase-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="ERASE" data-testid="start-fresh-confirm-input" />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="start-fresh-cancel-btn">Keep my data</AlertDialogCancel>
              <AlertDialogAction onClick={erase} disabled={busy || confirm !== "ERASE"} className="bg-destructive hover:bg-destructive/90" data-testid="start-fresh-confirm-btn">
                {busy ? "Erasing…" : "Yes, erase everything"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};
